import { Modal, Box, Typography, Button, MenuItem } from '@mui/material';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import { useForm } from 'react-hook-form';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { FormInputText } from '../../../form-components/FormInputText';
import { FormInputDropdown } from '../../form/FormInputDropdown';
import { createGolfArea, updateGolfAreaById } from '../../../api/golf';
import { useToast } from 'use-toast-mui';
import { errorMessages } from '../../../utils/methods';

const GolfAreaEditModal = ({
  open,
  handleClose,
  selectedArea,
  branchId,
  refreshSpotAreas,
  setSelectedAreaId,
  setValue,
}) => {
  const { t } = useTranslation();
  const { control, handleSubmit, reset, setValue: setFormValue } = useForm();
  const user = useSelector(state => state.user.user);
  const toast = useToast();
  const isEditMode = !!selectedArea;
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (selectedArea && open) {
      setFormValue('areaName', selectedArea.golfAreaInfo?.areaName || '');
      setFormValue('areaDescription', selectedArea.golfAreaInfo?.areaMemo || '');
      setFormValue('horizontalLine', selectedArea.golfAreaInfo?.columnCount?.toString() || '');
      setFormValue('verticalLine', selectedArea.golfAreaInfo?.rowCount?.toString() || '');
    } else if (!selectedArea && open) {
      reset();
    }
  }, [selectedArea, open, setFormValue, reset]);

  // 중복 방지
  const handleSubmitForm = async data => {
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    try {
      if (user?.token && branchId) {
        if (isEditMode) {
          console.log('selectedArea', selectedArea);
          const areaData = {
            areaName: data.areaName,
            areaMemo: data.areaDescription,
            columnCount: parseInt(data.horizontalLine),
            rowCount: parseInt(data.verticalLine),
          };
          try {
            const response = await updateGolfAreaById(user.token, selectedArea.golfAreaId, areaData);
            console.log('골프 구역 수정 상태', response);
          } catch (error) {
            toast.error(errorMessages(error));
            setIsSubmitting(false);
            return;
          }
        } else {
          const areaData = {
            golfAreaInfo: {
              areaName: data.areaName,
              areaMemo: data.areaDescription,
              columnCount: parseInt(data.horizontalLine),
              rowCount: parseInt(data.verticalLine),
            },
            branchId: branchId,
          };
          try {
            await createGolfArea(user.token, areaData);
          } catch (error) {
            toast.error(errorMessages(error));
            setIsSubmitting(false);
            return;
          }
        }

        if (refreshSpotAreas) {
          await refreshSpotAreas();
        }

        reset();
        setIsSubmitting(false);
        handleClose();
      }
    } catch (error) {
      console.log(isEditMode ? '골프 구역 수정 실패' : '골프 구역 생성 실패', error);
      setIsSubmitting(false);
    }
  };
  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '465px',
    height: '522px',
    bgcolor: 'background.paper',
    boxShadow: 24,
    borderRadius: '16px',
    overflow: 'hidden',
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      disableEscapeKeyDown
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description">
      <Box sx={modalStyle}>
        <Box
          sx={{
            height: '56px',
            backgroundColor: '#262626',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: '0 32px',
          }}>
          <Typography
            id="modal-modal-title"
            component="div"
            sx={{
              display: 'flex',
              flexDirection: 'tow',
              justifyContent: 'space-between',
              color: '#fff',
              fontSize: '20px',
              fontWeight: '600',
            }}>
            <Box sx={{ fontSize: '18px' }}>{isEditMode ? t('Area edit') : t('Area create')}</Box>
          </Typography>
          <CloseIcon sx={{ color: '#fff', width: '32px', height: '32px', cursor: 'pointer' }} onClick={handleClose} />
        </Box>
        <Box
          sx={{
            height: '386px',
            p: '32px 32px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
          }}>
          <Typography sx={{ fontSize: '16px', fontWeight: '500', color: '#000' }}>{t('Area name')}</Typography>
          <FormInputText
            control={control}
            name="areaName"
            placeholder={t('Please enter the area name')}
            rules={{ required: true }}
          />
          <Typography sx={{ fontSize: '16px', fontWeight: '500', color: '#000' }}>{t('Area description')}</Typography>
          <FormInputText
            control={control}
            name="areaDescription"
            placeholder={t('Please enter the area description')}
            rules={{ required: true }}
          />
          <Typography sx={{ fontSize: '16px', fontWeight: '500', color: '#000' }}>{t('Horizontal line')}</Typography>
          <FormInputDropdown
            control={control}
            name="horizontalLine"
            placeholder={t('Please Select')}
            rules={{ required: true }}
            disabledItem={<MenuItem disabled>{t('Please Select')}</MenuItem>}
            options={[
              { value: '1', label: '1' + t('line') },
              { value: '2', label: '2' + t('line') },
              { value: '3', label: '3' + t('line') },
              { value: '4', label: '4' + t('line') },
              { value: '5', label: '5' + t('line') },
              { value: '6', label: '6' + t('line') },
            ]}></FormInputDropdown>
          <Typography sx={{ fontSize: '16px', fontWeight: '500', color: '#000' }}>{t('Vertical line')}</Typography>
          <FormInputDropdown
            control={control}
            name="verticalLine"
            placeholder={t('Please Select')}
            rules={{ required: true }}
            disabledItem={<MenuItem disabled>{t('Please Select')}</MenuItem>}
            options={[
              { value: '1', label: '1' + t('line') },
              { value: '2', label: '2' + t('line') },
              { value: '3', label: '3' + t('line') },
              { value: '4', label: '4' + t('line') },
              { value: '5', label: '5' + t('line') },
              { value: '6', label: '6' + t('line') },
            ]}></FormInputDropdown>
        </Box>
        <Box sx={{ height: '80px', display: 'flex', justifyContent: 'center', gap: '16px' }}>
          <Button
            sx={{
              backgroundColor: '#D9D9D9',
              width: '120px',
              height: '48px',
              borderRadius: '16px',
              fontSize: '20px',
              color: '#000',
              '&:hover': { backgroundColor: '#A6A6A6' },
            }}
            onClick={() => {
              if (!isSubmitting) {
                handleClose();
                reset();
              }
            }}
            disabled={isSubmitting}>
            {t('Cancel')}
          </Button>
          <Button
            sx={{
              backgroundColor: isSubmitting ? '#A6A6A6' : '#58B9C1',
              width: '240px',
              height: '48px',
              borderRadius: '16px',
              fontSize: '20px',
              color: '#fff',
              '&:hover': { backgroundColor: isSubmitting ? '#A6A6A6' : '#307C82' },
              '&:disabled': {
                backgroundColor: '#A6A6A6',
                color: '#fff',
              },
            }}
            onClick={handleSubmit(handleSubmitForm)}
            disabled={isSubmitting}>
            {isSubmitting ? t('Processing...') : isEditMode ? t('Update Area') : t('Create Area')}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default GolfAreaEditModal;
